/**
 * Hook e helper condivisi per il selettore Unita' Organizzativa (UO).
 *
 * Usati da CaricoForm (magazzino) e RicezioneForm (ordini) per il campo
 * "Negozio / Unita' organizzativa" obbligatorio con default ultima usata
 * (cantiere lotti->UO, T4). Le opzioni vengono SEMPRE da GET
 * /api/v1/unita/attive: nessun hardcoding di liste (regola HARD).
 *
 * Vive in frontend/src/lib (alias @lib) perche' e' il punto condiviso gia'
 * usato dai moduli per hook cross-modulo (vedi useFormatters, useUrlState):
 * i moduli non si importano l'uno con l'altro, @floraltech/ui e' riservato
 * ai componenti puri, @lib e' dove la shell espone hook applicativi comuni.
 */

import { useEffect, useState } from 'react';

/** Unita' organizzativa attiva, cosi' come esposta da GET /api/v1/unita/attive. */
export interface UnitaAttiva {
  id: string;
  nome: string;
  tipo: string;
  codice: string | null;
}

const AUTH_HEADERS = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/**
 * Carica tutte le unita' organizzative attive del tenant con una singola
 * fetch al mount. Le UO sono poche per costruzione: il filtro per la ricerca
 * nel Combobox va fatto client-side sul risultato, non ri-fetchando.
 */
export function useUnitaAttive(): {
  unita: UnitaAttiva[];
  loading: boolean;
  error: string | null;
} {
  const [unita, setUnita] = useState<UnitaAttiva[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const resp = await fetch('/api/v1/unita/attive', {
          headers: AUTH_HEADERS(),
        });
        if (!resp.ok) {
          throw new Error(`Errore ${resp.status}`);
        }
        const data: UnitaAttiva[] = await resp.json();
        if (!cancelled) {
          setUnita(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Errore caricamento unita' organizzative",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { unita, loading, error };
}

/**
 * Chiave localStorage condivisa (magazzino + ordini) per l'ultima UO usata
 * dall'utente corrente. Namespace "magazzino" intenzionale: la preferenza
 * e' unica per utente a prescindere dal form che l'ha impostata.
 */
const ultimaUOKey = (userId: string): string =>
  `floraltech.magazzino.ultima_uo.${userId}`;

/** Legge l'ultima UO usata dall'utente corrente (null se assente). */
export function getUltimaUO(userId: string): string | null {
  try {
    return localStorage.getItem(ultimaUOKey(userId));
  } catch {
    return null;
  }
}

/** Salva l'ultima UO usata dall'utente corrente. */
export function setUltimaUO(userId: string, unitaId: string): void {
  try {
    localStorage.setItem(ultimaUOKey(userId), unitaId);
  } catch {
    /* localStorage non disponibile (quota, private mode): non bloccante. */
  }
}
